import {useEffect, useState} from "react";

import {
    allClassTypeOptions,
    group,
    isGroupTypeClass,
    isOnlineTypeClass,
    isPrivateTypeClass,
} from "conts/profileConst.js";
import styled from "styled-components";

import {teacherLocalKey, teacherStepLocalKey, useTeacherCreation,} from "context/TeacherCreationContext.jsx";
import {useToast} from "hooks/index.js";
import {appRoute} from "pages/app/app.routes";
import {useNavigate} from "react-router-dom";
import {HelperText} from "ui/base/helperText/HelperText.jsx";
import {InputSignUp} from "ui/base/InputSignUp/InputSignUp.jsx";
import {SelectButton} from "ui/base/selectInput/SelectButton.jsx";
import {Tooltip} from "ui/base/tooltip/Tooltip.jsx";
import {DollarIcon} from "ui/icons/inputIcons/DollarIcon.jsx";
import {ProfileIcon} from "ui/icons/inputIcons/ProfileIcon";
import {numberOnly, safeLocalStorage} from "utils/index.js";
import {ButtonPrimary} from "ui/base/Button/ButtonStyled";
import {defaultThemeColors} from "ui/theme";
import {pixelTrackEvent} from "utils/facebookPixel.js";
import Counter from "ui/base/selectInput/counter";

const MINIMUM_CLASS_PRICE = 20;
const MAXIMUM_CLASS_PRICE = 5000;

const TEXT_ESTIMATED_EARNINGS =
    "Your earnings after Teache's 20% service fee. Actual earnings may be further reduced by applicable taxes.";

const TEXT_GROUP =
    "Group classes are sessions where multiple people can book the same class. They are generally less expensive than Private classes.";

export const TypeAndPrice = () => {
    const navigate = useNavigate();

    const {
        formData,
        changeFormData: changeTeacherInfo,
        setFormData,
    } = useTeacherCreation();

    const [formDataErrors, setFormDataErrors] = useState({});
    const [isNextButtonDisabled, setNextButttonDisabled] = useState(true);
    const filteredOptions = allClassTypeOptions.filter(
        (option) => option.online_type === formData.online_type
    );

    const [selectedOption, setSelectedOption] = useState(allClassTypeOptions
        .find((option) => option.type_of_class === formData?.type_of_class && option.online_type === formData.online_type
        ) || filteredOptions[0]);

    const onlineType = formData?.online_type || "offline";

    const isGroupClass = isGroupTypeClass(selectedOption?.label);
    const isPrivateClass = isPrivateTypeClass(selectedOption?.label);
    const isOnlineClass = isOnlineTypeClass(selectedOption?.label)

    const validatePrices = (key, price) => {
        let errorMessage = "";

        if (price === "") {
            errorMessage = "Price must be filled";
        } else if (price < MINIMUM_CLASS_PRICE) {
            errorMessage = "Minimum price per class is $20";
        } else if (price > MAXIMUM_CLASS_PRICE) {
            errorMessage = "Maximum price per class is $5000";
        }

        setFormDataErrors((prevState) => ({
            ...prevState,
            [key]: errorMessage,
        }));
        return {[key]: errorMessage};
    };

    const validateParticipants = (key, value) => {
        let errorMessage = "";

        if (value < 1) {
            errorMessage = "Minimum number of participants is 1";
        } else if (value > 20) {
            errorMessage = "Maximum number of participants is 20";
        }

        setFormDataErrors((prevState) => ({
            ...prevState,
            [key]: errorMessage,
        }));
        return {[key]: errorMessage};
    };

    const changeFormData = (key, value) => {
        changeTeacherInfo(key, value);
        if (key === "price_per_group_class" || key === "price_per_private_class" || key === "price_per_online_class") {
            validatePrices(key, value);
        }
        if (
            key === "number_of_students" ||
            key === "number_of_students_private_class"
        ) {
            validateParticipants(key, value);
        }
    };

    const resetGroupData = () => {
        setFormData((prevData) => ({
            ...prevData,
            price_per_group_class: "",
            number_of_students: 2,
            price_per_group_class_estimate_earning: 0,
        }));
        setFormDataErrors((prevState) => ({
            ...prevState,
            price_per_group_class: "",
            number_of_students: "",
            price_per_group_class_estimate_earning: "",
        }));
    };

    const resetOnlineData = () => {
        setFormData((prevData) => ({
            ...prevData,
            price_per_online_class: "",
            price_per_online_class_estimate_earning: 0,
        }));
        setFormDataErrors((prevState) => ({
            ...prevState,
            price_per_online_class: "",
            price_per_group_class_estimate_earning: "",
        }));
    };

    const resetPrivateData = () => {
        setFormData((prevData) => ({
            ...prevData,
            price_per_private_class: "",
            number_of_students_private_class: 1,
            price_per_private_class_estimate_earning: 0,
        }));
        setFormDataErrors((prevState) => ({
            ...prevState,
            price_per_private_class: "",
            number_of_students_private_class: "",
            price_per_private_class_estimate_earning: "",
        }));
    };

    const handleSelectTypeOfClass = (classType) => {
        setSelectedOption(classType)
        changeFormData("type_of_class", classType.type_of_class);

        if (isGroupClass) {
            resetPrivateData();
            resetOnlineData()
        }
        if (isPrivateClass) {
            resetOnlineData()
            resetGroupData();
        }
        if (isOnlineClass) {
            resetPrivateData();
            resetGroupData();
        }
    };

    const onSubmit = () => {
        if (formData.profile_pic && formData.profile_pic.uri.length === 0) {
            useToast("error", "Step 1: Upload profile picture");
            return;
        }

        let submitErrors = {};

        if (isGroupClass && onlineType != "online") {
            submitErrors = {
                ...submitErrors,
                ...validatePrices(
                    "price_per_group_class",
                    formData.price_per_group_class
                ),
            };
            submitErrors = {
                ...submitErrors,
                ...validateParticipants(
                    "number_of_students",
                    formData.number_of_students
                ),
            };
        }

        if (isPrivateClass && onlineType != "online") {
            submitErrors = {
                ...submitErrors,
                ...validatePrices(
                    "price_per_private_class",
                    formData.price_per_private_class
                ),
            };
            submitErrors = {
                ...submitErrors,
                ...validateParticipants(
                    "number_of_students_private_class",
                    formData.number_of_students_private_class
                ),
            };
        }

        if (isOnlineClass) {
            submitErrors = {
                ...submitErrors,
                ...validatePrices(
                    "price_per_online_class",
                    formData.price_per_online_class
                ),
            };
        }

        // Check for any errors
        const hasErrors = Object.values(submitErrors).some((error) => {
            return error !== "";
        });

        if (
            submitErrors["price_per_private_class"] ||
            submitErrors["price_per_group_class"] ||
            submitErrors["price_per_online_class"]
        ) {
            useToast(
                "error",
                submitErrors["price_per_private_class"] ||
                submitErrors["price_per_group_class"] ||
                submitErrors["price_per_online_class"]
            );
            return;
        } else if (hasErrors) {
            useToast("error", "Fill all the fields");
            return;
        }

        safeLocalStorage.setItem(teacherLocalKey, formData);
        safeLocalStorage.setItem(teacherStepLocalKey, 3);
        navigate(appRoute.completeProfileThird.fullPath);
    };
    useEffect(() => {
        pixelTrackEvent("Enter Instructor Sign-up Page 3");
    }, []);


    useEffect(() => {
        const check = () => {
            let objectToCheck = {};

            if (isOnlineClass) {
                objectToCheck.price_per_online_class = formData.price_per_online_class;

                objectToCheck.price_per_online_class_estimate_earning = formData.price_per_online_class_estimate_earning
            }

            if (isPrivateClass) {
                objectToCheck.price_per_private_class = formData.price_per_private_class;
                objectToCheck.number_of_students_private_class = formData.number_of_students_private_class;
                objectToCheck.price_per_private_class_estimate_earning = formData.price_per_private_class_estimate_earning;
            }

            if (isGroupClass) {
                objectToCheck.number_of_students = formData.number_of_students;
                objectToCheck.price_per_group_class = formData.price_per_group_class;
                objectToCheck.price_per_group_class_estimate_earning = formData.price_per_group_class_estimate_earning;
            }

            const TARGET_KEY_STRING = ["price_per_private_class", "price_per_group_class", "price_per_online_class"]
            const keysOfOriginalPrice = Object.keys(objectToCheck).filter(key => TARGET_KEY_STRING.includes(key));
            const originalPrices = keysOfOriginalPrice.map(key => objectToCheck[key])

            const isEmpty = Object.values(objectToCheck).some(
                value => {
                    return value === null || value === undefined || value === '' || value === 0
                }
            ) || originalPrices.some(price => price < MINIMUM_CLASS_PRICE);

            setNextButttonDisabled(isEmpty);
        }

        check()
    }, [formData])


    return (
        <Container>
            <div>
                <FormContainer>
                    {onlineType === "online" ? null : <SelectButton
                        bgColor="blue15"
                        valueColor="blue"
                        placeholderColor="blue"
                        borderColor="transparent"
                        label="What type of class do you offer?"
                        options={filteredOptions}
                        value={selectedOption}
                        onChange={handleSelectTypeOfClass}
                    />}


                    <RowInput>
                        {isPrivateClass ? (
                            <Counter
                                value={formData.number_of_students_private_class}
                                icon={<ProfileIcon/>}
                                label="Set a participant limit for a Private class"
                                maxToCount={20}
                                onChange={(value) => {
                                    changeFormData(
                                        "number_of_students_private_class",
                                        value ? +value : ""
                                    );
                                }}
                                isError={formDataErrors?.number_of_students_private_class}
                                errorText={formDataErrors?.number_of_students_private_class}
                            />
                        ) : null}

                        {isGroupClass ? (
                            <Counter
                                value={formData.number_of_students}
                                icon={<ProfileIcon/>}
                                label="Set a participant limit for a Group class"
                                minToCount={2}
                                maxToCount={20}
                                onChange={(value) => {
                                    changeFormData("number_of_students", value ? +value : "");
                                }}
                                isError={formDataErrors?.number_of_students}
                                errorText={formDataErrors?.number_of_students}
                            />
                        ) : null}
                    </RowInput>
                    {isPrivateClass ? (
                        <div>
                            <RowInput>
                                <InputSignUp
                                    type="number"
                                    inputmode="numeric"
                                    icon={<DollarIcon/>}
                                    id="price_per_private_class"
                                    onInput={numberOnly}
                                    label="Price per Private class"
                                    placeholder="Enter cost"
                                    value={formData.price_per_private_class}
                                    setValue={(value) => {
                                        changeFormData(
                                            "price_per_private_class",
                                            value ? +value : ""
                                        );
                                        changeFormData(
                                            "price_per_private_class_estimate_earning",
                                            +(+value * 0.8).toFixed(2)
                                        );
                                    }}
                                    maxLength={4}
                                />

                                <EstimateContainer>
                                    <HelperText>Estimated earnings</HelperText>
                                    <EstimateInnerContainer>
                                        <Estimate>
                                            <DollarIcon/>
                                            <HelperText sizeMobile="regular" color="blue">
                                                {formData.price_per_private_class_estimate_earning}
                                            </HelperText>
                                            <Tooltip
                                                text={TEXT_ESTIMATED_EARNINGS}
                                                place="right"
                                                id="estimate-private"
                                            />
                                        </Estimate>
                                    </EstimateInnerContainer>
                                </EstimateContainer>
                            </RowInput>
                            {formDataErrors?.price_per_private_class && (
                                <HelperText color="pink" size="small" textAlign="left">
                                    {formDataErrors?.price_per_private_class}
                                </HelperText>
                            )}
                        </div>
                    ) : null}

                    {isGroupClass ? (
                        <div>
                            <RowInput>
                                <InputSignUp
                                    type="number"
                                    inputmode="numeric"
                                    icon={<DollarIcon/>}
                                    id="price_per_group_class"
                                    onInput={numberOnly}
                                    label="Price per Group class"
                                    placeholder="Enter cost"
                                    value={formData.price_per_group_class}
                                    setValue={(value) => {
                                        changeFormData(
                                            "price_per_group_class",
                                            value ? +value : ""
                                        );
                                        changeFormData(
                                            "price_per_group_class_estimate_earning",
                                            +(+value * 0.8).toFixed(2)
                                        );
                                    }}
                                    maxLength={4}
                                    Tooltip={<Tooltip
                                        text={TEXT_GROUP}
                                        place="top"
                                        id="group"
                                        size={20}
                                    />}
                                />

                                <EstimateContainer>
                                    <HelperText>Estimated earnings</HelperText>
                                    <EstimateInnerContainer>
                                        <Estimate>
                                            <DollarIcon/>
                                            <HelperText sizeMobile="regular" color="blue">
                                                {formData.price_per_group_class_estimate_earning}
                                            </HelperText>
                                            <Tooltip
                                                text={TEXT_ESTIMATED_EARNINGS}
                                                place="right"
                                                id="estimate-group"
                                            />
                                        </Estimate>
                                    </EstimateInnerContainer>
                                </EstimateContainer>
                            </RowInput>
                            {formDataErrors?.price_per_group_class && (
                                <HelperText color="pink" size="extraSmall" textAlign="left">
                                    {formDataErrors?.price_per_group_class}
                                </HelperText>
                            )}
                        </div>
                    ) : null}

                    {isOnlineClass ? (
                        <>
                            <Container>
                                <FormContainer>
                                    <div>
                                        <RowInput>
                                            <InputSignUp
                                                type="number"
                                                inputmode="numeric"
                                                icon={<DollarIcon/>}
                                                id="price_per_online_class"
                                                onInput={numberOnly}
                                                label="Price per Online class"
                                                placeholder="Enter cost"
                                                value={formData.price_per_online_class}
                                                setValue={(value) => {
                                                    changeFormData("price_per_online_class", value ? +value : "");
                                                    changeFormData(
                                                        "price_per_online_class_estimate_earning",
                                                        +(+value * 0.8).toFixed(2)
                                                    );
                                                }}
                                                maxLength={4}
                                            />

                                            <EstimateContainer>
                                                <HelperText>Estimated earnings</HelperText>
                                                <EstimateInnerContainer>
                                                    <Estimate>
                                                        <DollarIcon/>
                                                        <HelperText sizeMobile="regular" color="blue">
                                                            {formData.price_per_online_class_estimate_earning}
                                                        </HelperText>
                                                        <Tooltip
                                                            text={TEXT_ESTIMATED_EARNINGS}
                                                            place="right"
                                                            id="estimate-private"
                                                        />
                                                    </Estimate>
                                                </EstimateInnerContainer>
                                            </EstimateContainer>
                                        </RowInput>
                                        {formDataErrors?.price_per_online_class && (
                                            <HelperText color="pink" size="small" textAlign="left">
                                                {formDataErrors?.price_per_online_class}
                                            </HelperText>
                                        )}
                                    </div>
                                </FormContainer>
                            </Container>
                        </>
                    ) : null}
                </FormContainer>

                <InputContainer>
                    <div style={{width: "100%", display: "flex"}}>
                        <ButtonPrimary
                            disabled={isNextButtonDisabled}
                            onClick={() => {
                                pixelTrackEvent(
                                    "Instructor Sign-up Page 3: Clicked on Next step"
                                );
                                onSubmit();
                            }}
                        >
                            Next Step
                        </ButtonPrimary>
                    </div>
                </InputContainer>
            </div>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;

    & > div {
        max-width: 650px;
        display: grid;
        gap: 1rem;
    }
`;

const FormContainer = styled.div`
    display: grid;
    gap: 1rem;
    grid-column: span 2;
    height: fit-content;
    @media (min-width: 1024px) {
        grid-column: span 1;
    }
`;

const RowInput = styled.div`
    width: 100%;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    place-items: start;
    gap: 1rem;
`;

const InputContainer = styled.div`
    grid-column: span 2;
`;

const EstimateContainer = styled.div`
    display: grid;
    gap: 1rem;
`;
const EstimateInnerContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
`;
const Estimate = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 10px;
    color: ${defaultThemeColors.placeholder};
    height: 62px;
`;
